import { useCallback, useSyncExternalStore } from "react";
import { useRouterState } from "@tanstack/react-router";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useQuickStartStore } from "@/stores/useQuickStartStore";
import { useGuidesStore } from "@/stores/useGuidesStore";

export type TourVariant = "new-user" | "setup-complete" | "none";

const MIN_DESKTOP_WIDTH = 1024;

const desktopMql =
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia(`(min-width: ${MIN_DESKTOP_WIDTH}px)`)
    : null;

function subscribeDesktop(cb: () => void) {
  if (!desktopMql) return () => {};
  desktopMql.addEventListener("change", cb);
  return () => desktopMql.removeEventListener("change", cb);
}
function getDesktopSnapshot() {
  return desktopMql?.matches ?? false;
}
function getDesktopServerSnapshot() {
  return false;
}

function seenKey(authId: string) {
  return `paperchase-quickstart-seen-${authId}`;
}

function readSeen(authId: string): boolean {
  try {
    return localStorage.getItem(seenKey(authId)) === "1";
  } catch {
    return true; // treat as seen — safer than crashing the tour
  }
}

function writeSeen(authId: string): void {
  try {
    localStorage.setItem(seenKey(authId), "1");
  } catch {
    // silently ignore
  }
}

function isCurateRoute(pathname: string): boolean {
  // Matches "/concepts/{id}/curate" (new) or "/concepts/{id}/expand" (legacy redirect)
  return /^\/concepts\/[^/]+\/(curate|expand)\/?$/.test(pathname);
}

export function useTourState() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data: currentUser } = useCurrentUser();

  const conceptJustCreated = useQuickStartStore((s) => s.conceptJustCreated);
  const setupJustCompleted = useQuickStartStore((s) => s.setupJustCompleted);
  const replaySessionOverride = useQuickStartStore((s) => s.replaySessionOverride);
  const catalogOpen = useGuidesStore((s) => s.catalogOpen);
  const openWalkthroughId = useGuidesStore((s) => s.openWalkthroughId);

  const authId = currentUser?.auth_id;

  const isDesktop = useSyncExternalStore(
    subscribeDesktop,
    getDesktopSnapshot,
    getDesktopServerSnapshot,
  );
  const guideUiOpen = catalogOpen || openWalkthroughId !== null;

  const seen = authId ? readSeen(authId) : true; // treat missing user as already-seen (never fire)

  const baseGuards = !!authId && isDesktop && !guideUiOpen;
  const canShow = baseGuards && (!seen || replaySessionOverride);

  let variant: TourVariant = "none";
  if (canShow && replaySessionOverride) {
    // Replay always fires the new-user variant. CorpusSeedingPanel is required
    // as the spotlight target; replay on a route where it's not mounted will
    // fall back to a centered tooltip (acceptable for a deliberate replay).
    variant = "new-user";
  } else if (baseGuards && isCurateRoute(pathname) && setupJustCompleted) {
    // Fires even when the quickstart was already seen: finishing guided setup is
    // its own moment, and most ring users saw the quickstart long ago.
    variant = "setup-complete";
  } else if (canShow && isCurateRoute(pathname) && conceptJustCreated) {
    variant = "new-user";
  }

  const markSeen = useCallback(() => {
    if (authId) {
      writeSeen(authId);
    }
  }, [authId]);

  const replay = useCallback(() => {
    useQuickStartStore.getState().replay();
  }, []);

  return {
    shouldShow: variant !== "none",
    variant,
    markSeen,
    replay,
  };
}
